import { BaseSchedule } from "./BaseSchedule";
import { AnyAggregator } from "./PurchaseRule";

export interface RuleFilter {
    object: string;
    operator?: string;
    errorMessage?: string;
    validationLevel?: ValidationLevel;
}

export enum PurchaseRuleFilterObject {
    OR_FILTER = "or_filter",
    AND_FILTER = "and_filter",
    IN_FILTER = "in_filter",
    REGEX_MATCH_FILTER = "regex_match_filter",
    IN_SCHEDULE_FILTER = "in_schedule_filter",
    IN_PRODUCT_SCHEDULE_FILTER = "in_product_schedule_filter",
    EQUALS_FILTER = "equals_filter",
    NOT_EQUALS_FILTER = "not_equals_filter",
    GREATER_THAN_FILTER = "greater_than_filter",
    LESSER_THAN_FILTER = "lesser_than_filter",
    GREATER_THAN_OR_EQUAL_TO_FILTER = "greater_than_or_equal_to_filter",
    LESSER_THAN_OR_EQUAL_TO_FILTER = "lesser_than_or_equal_to_filter",
}

export enum PurchaseRuleFilterOperator {
    IN_PRODUCT_SCHEDULE = "in_product_schedule",
    EQUALS = "equals",
    NOT_EQUALS = "not_equals",
    AND = "and",
    GT = "gt",
    LT = "lt",
    GTE = "gte",
    LTE = "lte",
    IN = "in",
}

export type RuleFilterItem =
    | InScheduleFilter
    | InFilter
    | RegexMatchFilter
    | InProductScheduleFilter
    | GreaterThanOrEqualToFilter
    | GreaterThanFilter
    | LesserThanFilter
    | NotEqualsFilter
    | EqualsFilter
    | OrFilter
    | MinimumTransitDurationFilter
    | ArrivalSpanFilter
    | SameDayArrivalFilter
    | AllFilter;

export interface AndFilter extends RuleFilter {
    object: "and_filter";
    operator: "and";
    filters: Array<RuleFilterItem>;
}

export interface OrFilter extends RuleFilter {
    object: "or_filter";
    operator: "or";
    filters: Array<RuleFilterItem>;
}

export interface InScheduleFilter extends RuleFilter {
    object: "in_schedule_filter";
    operator: "in_schedule";
    field: string;
    schedules: BaseSchedule[];
    validationLevel?: ValidationLevel;
    filters?: Array<RuleFilterItem>;
}

export interface InProductScheduleFilter extends RuleFilter {
    object: PurchaseRuleFilterObject.IN_PRODUCT_SCHEDULE_FILTER;
    operator: PurchaseRuleFilterOperator.IN_PRODUCT_SCHEDULE;
    schedules: {
        all: boolean;
        items: ID[];
    };
    product: {
        id: ID;
    };
    manualTimeslotResolution?: boolean;
    filters?: Array<RuleFilterItem>;
}

export interface GreaterThanFilter extends RuleFilter {
    object: PurchaseRuleFilterObject.GREATER_THAN_FILTER;
    operator: PurchaseRuleFilterOperator.GT;
    field?: string;
    aggregator?: AnyAggregator;
    operand: number;
}

export interface LesserThanFilter extends RuleFilter {
    object: PurchaseRuleFilterObject.LESSER_THAN_FILTER;
    operator: PurchaseRuleFilterOperator.LT;
    field?: string;
    aggregator?: AnyAggregator;
    operand: number;
}

export interface GreaterThanOrEqualsToFilter extends RuleFilter {
    object: PurchaseRuleFilterObject.GREATER_THAN_OR_EQUAL_TO_FILTER;
    operator: PurchaseRuleFilterOperator.GTE;
    field: string;
    operand: number;
}

export interface LesserThanOrEqualsToFilter extends RuleFilter {
    object: PurchaseRuleFilterObject.LESSER_THAN_OR_EQUAL_TO_FILTER;
    operator: PurchaseRuleFilterOperator.LTE;
    field: string;
    operand: number;
}

export interface EqualsFilter extends RuleFilter {
    object: PurchaseRuleFilterObject.EQUALS_FILTER;
    operator?: PurchaseRuleFilterOperator.EQUALS;
    operand: ID;
    field: string;
    operandAggregator?: AnyAggregator;
}

export interface NotEqualsFilter extends RuleFilter {
    object: PurchaseRuleFilterObject.NOT_EQUALS_FILTER;
    operator?: PurchaseRuleFilterOperator.NOT_EQUALS;
    operand: ID;
    field: string;
}

export interface InFilter extends RuleFilter {
    object: PurchaseRuleFilterObject.IN_FILTER;
    operator: "in";
    operand: Array<string | null> | null;
    operandAggregator?: AnyAggregator;
    validationLevel?: ValidationLevel.ERROR | ValidationLevel.INFO;
    field: string;
    filters?: Array<RuleFilterItem>;
}

export interface AllFilter extends RuleFilter {
    object: "all_filter";
    operator: "all";
    operand: Array<string | null> | null;
    field: string;
    filters?: Array<RuleFilterItem>;
}

export interface RegexMatchFilter extends RuleFilter {
    object: "regex_match_filter";
    operator: "in";
    operand: string;
    field: string;
}

export interface GreaterThanOrEqualToFilter extends RuleFilter {
    object: "greater_than_or_equal_to_filter";
    aggregator: Record<string, unknown>;
    operand: number;
}

export const enum ProductFilterField {
    PRODUCT = "product.id",
    PARENT_PRODUCT = "parentProduct.id",
}

export enum ValidationLevel {
    ERROR = "error",
    INFO = "info",
    WARNING = "warning",
    IGNORE = "ignore",
}

export interface ArrivalSpanFilter extends RuleFilter {
    object: "arrival_span_filter";
    operator: "arrival_span";
    operand: number;
    field: "arrivalDateTime";
}
export interface SameDayArrivalFilter extends RuleFilter {
    object: "same_day_arrival_filter";
    operator: "same_day_arrival";
    field: "arrivalDateTime";
}
export interface MinimumTransitDurationFilter extends RuleFilter {
    object: "minimum_transit_duration_filter";
    operand: number;
    field: "arrivalDateTime";
}

export enum PurchaseRuleFilterError {
    VALIDATION_ERROR = "purchase_rule_validation_error",
}

export enum PurchaseRuleFilterErrorCode {
    IN_FILTER_NOT_MET = "in_filter_not_met",
    GREATER_THAN_OR_EQUAL_TO_FILTER_NOT_MET = "greater_than_or_equal_to_filter_not_met",
}
